import { createColumnHelper } from '@tanstack/react-table'
import { IYieldCompounding } from '~/@api/generated-api-types-project/Api'
import { formatAmount } from '~/@lib/utils/number.utils'
import { TextTableHead } from '~/@mid_ui/typography/TextTableHead'
import { TextTableCell } from '~/@mid_ui/typography/TextTableCell'
import { StageLabel } from '~/@mid_ui/typography/StageLabel'
import { getProjectInvestmentPeriodString } from '~/@pages_investing/_utils/investmentPeriod.utils'
import { minMaxFilter } from '~/@pages_investing/projects/_projects_filtering/filtering.utils'
import {
  YieldPerAnnumFilterFn,
  YieldPerAnnumSortingFn,
} from '~/@pages_investing/projects/_projects_filtering/filtering.utils'
import { ProjectBlurredContent } from '../_projects_ui/project-blurred-content/ProjectBlurredContent'
import { projectTooltipByType } from '../_projects_ui/project-info-tooltips/projectTooltipByType'
import { ProjectVipBadge } from '../_projects_ui/project-vip-badge/ProjectVipBadge'
import { HotBonusComponent } from '../_projects_ui/hot-bonus/HotBonusComponent'
import { BoosterProjectTooltip } from '../_projects_ui/booster-project/BoosterProjectTooltip'
import { ProjectCellContainer } from '../_projects_ui/project-cell-container/ProjectCellContainer'
import type { ProjectPaymentTypeLabels } from '../_projects_ui/project-payment-type-label/projectPaymentType'
import { ProjectPreviewImageAndPopupCell } from '../_projects_ui/project-preview-and-label/ProjectPreviewImageAndPopupCell'
import {
  ProjectYieldText,
  type ProjectWithSchemAndExtraYieldSettings,
} from '../_projects_ui/project-yield-text/ProjectYieldText'
import type { ProjectsItem } from '../_project_types/project.types'
import type { ProjectLoanPurpose } from '../_project_types/projectLoanPurpose'
import type { ProjectCollateralSubcategory } from '../_project_types/projectCollateralSubCategory'
import type { ProjectsTableMeta } from './useRunningProjectsTable'
import { TextTooltip } from '~/@mid_ui/text-tooltip/TextTooltip'
import { FeeImmunityBadge } from '../../fee-immunity/FeeImmunityBadge'
import { getProjectLoanPurposeLabel } from '../_projects_ui/project-loan-purpose/labelByProjectLoanPurpose'
import { transformSecurity } from '~/@lib/utils/riskScoreMapper'
import { ProjectPaymentTypeLabel } from '../_projects_ui/project-payment-type-label/ProjectPaymentTypeLabel'
import { hasCustomText } from '~/@pages_investing/project-detail/project-detail_ui/detail-html-value/hasCustomText'
import { DetailHtmlValue } from '~/@pages_investing/project-detail/project-detail_ui/detail-html-value/DetailHtmlValue'

const columnHelper = createColumnHelper<ProjectsItem>()

const hiddenColumns = [
  columnHelper.accessor('amountTarget', {
    id: 'amountTarget',
    filterFn: minMaxFilter,
  }),
]

const columns = [
  columnHelper.accessor('loanPurpose', {
    id: 'loanPurpose',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead className="ml-4">
          &nbsp;
          <br />
          Účel úveru
        </TextTableHead>
        {projectTooltipByType.loanPurpose}
      </ProjectCellContainer>
    ),
    cell: (props) => {
      const project = props.row.original as unknown as ProjectsItem
      return (
        <div className="flex items-center gap-6">
          <div className="flex h-full w-4! items-center justify-center">
            <ProjectVipBadge
              blurTagNames={project.blurTagNames || []}
              blurTags={project.blurTags || []}
            />
          </div>
          <TextTableCell className="flex flex-col items-start justify-center gap-1">
            <p>
              {project.loanPurpose != null
                ? getProjectLoanPurposeLabel(project.loanPurpose)
                : '-'}
            </p>
            <p className="text-sm  text-accent underline">
              {props.row.original.businessId}
            </p>
          </TextTableCell>
        </div>
      )
    },
    filterFn: (row, columnId, filterValue: ProjectLoanPurpose[]) => {
      if (!filterValue.length) return true

      return filterValue.includes(row.getValue(columnId))
    },
  }),
  columnHelper.accessor('collateralInfo.collateralSubcategory', {
    id: 'collateralSubcategory',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          &nbsp;
          <br />
          Zaistenie
        </TextTableHead>
        {projectTooltipByType.category}
      </ProjectCellContainer>
    ),
    cell: ({ row: { original: project } }) => (
      <TextTableCell className="flex flex-row gap-2.5">
        <ProjectPreviewImageAndPopupCell project={project} />
      </TextTableCell>
    ),
    filterFn: (row, columnId, filterValue: ProjectCollateralSubcategory[]) => {
      if (!filterValue.length) return true

      return filterValue.includes(row.getValue(columnId))
    },
  }),
  columnHelper.accessor('yieldPerAnnum', {
    id: 'interestRate',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Úrok ako <br /> predpokladaný výnos
        </TextTableHead>
        {projectTooltipByType.yield}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const { investorWallet, hasFeeImmunity, formatFeeSaving } = info.table
        .options.meta as ProjectsTableMeta
      const hotBonus = info.row.original.hotBonus
      const project = info.row.original as ProjectWithSchemAndExtraYieldSettings
      const { minYieldValue, maxYieldValue } = project._calculatedValues
      const yieldForFeeCalc = maxYieldValue > 0 ? maxYieldValue : minYieldValue
      const feeImmunityBadgeValue = formatFeeSaving?.(yieldForFeeCalc)
      const availableFutureRevenueFixed =
        info.row.original.availableFutureRevenueFixed

      return (
        <ProjectBlurredContent sendCellInfo={info}>
          <div className="flex flex-col gap-2 items-start">
            <TextTableCell className="flex flex-row items-center gap-1 text-green">
              <ProjectYieldText
                _calculatedValues={project._calculatedValues}
                isYieldCompoundingAnnually={
                  project.yieldCompounding === IYieldCompounding.Annually
                }
                yieldIconSize={22}
              />
              {project.investmentFutureRevenueEnabled &&
                investorWallet != null && (
                  <BoosterProjectTooltip
                    investorWallet={investorWallet}
                    availableFutureRevenueFixed={availableFutureRevenueFixed}
                  />
                )}
            </TextTableCell>
            {hasFeeImmunity && (
              <FeeImmunityBadge
                value={feeImmunityBadgeValue}
                minYieldValue={minYieldValue}
                maxYieldValue={maxYieldValue}
                isCompoundingAnnually={
                  project.yieldCompounding === IYieldCompounding.Annually
                }
              />
            )}
            <HotBonusComponent {...hotBonus} />
          </div>
        </ProjectBlurredContent>
      )
    },
    sortingFn: YieldPerAnnumSortingFn,
    filterFn: YieldPerAnnumFilterFn,
  }),
  columnHelper.accessor(
    (row) => Math.max(row.amountTarget - row.amountCurrent, 0),
    {
      id: 'amountMax',
      header: () => (
        <ProjectCellContainer>
          <div className="flex items-center justify-start gap-2">
            <TextTableHead>
              Ostáva <br /> investovať
            </TextTableHead>
            <StageLabel className="mb-1">Tranža</StageLabel>
          </div>
          {projectTooltipByType.amountMax}
        </ProjectCellContainer>
      ),
      cell: (info) => {
        const value = info.getValue()

        return (
          <ProjectBlurredContent sendCellInfo={info}>
            <div className="flex items-center gap-2">
              <TextTableCell>{formatAmount(value)}</TextTableCell>
              {info.row.original.stageNumber && (
                <StageLabel>{info.row.original.stageNumber}.</StageLabel>
              )}
            </div>
          </ProjectBlurredContent>
        )
      },
    }
  ),
  columnHelper.accessor('paymentTypeId', {
    id: 'paymentTypeId',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Typ <br /> výnosu
        </TextTableHead>
        {projectTooltipByType.paymentType}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const isCompounding =
        info.row.original.yieldCompounding === IYieldCompounding.Annually
      return (
        <div className="flex flex-row gap-2">
          <TextTableCell>
            <ProjectPaymentTypeLabel
              projectPaymentType={info.getValue() as ProjectPaymentTypeLabels}
              isYieldCompounding={isCompounding}
            />
          </TextTableCell>
        </div>
      )
    },
    filterFn: (row, columnId, filterValues: unknown[]) => {
      if (!filterValues.length) return true

      return filterValues.includes(row.getValue(columnId))
    },
  }),
  columnHelper.accessor('investmentPeriod', {
    id: 'investmentPeriod',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Doba <br /> úročenia
        </TextTableHead>
        {projectTooltipByType.investmentPeriod}
      </ProjectCellContainer>
    ),
    cell: (info) => (
      <TextTableCell className="flex flex-row gap-1">
        {`${getProjectInvestmentPeriodString(
          info.getValue(),
          info.row.original.yieldCompounding
        )} m`}
        {info.row.original.yieldCompounding === IYieldCompounding.Annually && (
          <TextTooltip text="Minimálna a maximálna doba investície. Možnosť predčasne ukončiť investíciu po 12, 24, 36 a 48 mesiacov." />
        )}
      </TextTableCell>
    ),
    filterFn: minMaxFilter,
  }),
  columnHelper.accessor('ltlPercentage', {
    id: 'ltlPercentage',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          &nbsp;
          <br />
          LTL
        </TextTableHead>
        {projectTooltipByType.ltl}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const value = info.getValue()
      const ltlPercentageCustom =
        info.row.original.collateralInfo.ltlPercentageCustom
      return (
        <TextTableCell>
          {value ? (
            `${value} %`
          ) : hasCustomText(ltlPercentageCustom) ? (
            <TextTooltip
              text={
                <DetailHtmlValue
                  html={ltlPercentageCustom}
                  className="text-[13px] text-primary text-left"
                />
              }
            />
          ) : (
            ''
          )}
        </TextTableCell>
      )
    },
    filterFn: minMaxFilter,
  }),
  columnHelper.accessor('riskScore', {
    id: 'riskScore',
    header: () => (
      <ProjectCellContainer>
        <TextTableHead>
          Skóre <br /> zabezpečenia
        </TextTableHead>
        {projectTooltipByType.riskScore}
      </ProjectCellContainer>
    ),
    cell: (info) => {
      const riskScore = info.getValue()
      return (
        <TextTableCell>
          {riskScore != null ? transformSecurity(riskScore) : '-'}
        </TextTableCell>
      )
    },
    filterFn: minMaxFilter,
  }),
  ...hiddenColumns,
]

export { hiddenColumns, columns }
